import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import mapValues from "lodash/mapValues";
import { FavoriteFields } from "@graylog/server-api";
import { StreamsActions } from "views/stores/StreamsStore";
import UserNotification from "util/UserNotification";
import useSendFavoriteFieldTelemetry from "components/common/message/details/fields/hooks/useSendFavoriteFieldTelemetry";
const useMessageFavoriteFieldsMutation = (initialFavoriteFieldsByStream, initialFavoriteFields) => {
  const sendFavoriteFieldTelemetry = useSendFavoriteFieldTelemetry();
  const { isPending: setFieldsIsPending, mutate: setFavoriteFields } = useMutation({
    mutationFn: (props) => FavoriteFields.set(props),
    onSuccess: (_, newFavoriteFieldsByStream) => {
      sendFavoriteFieldTelemetry("EDIT_SAVED", {
        fields_lengths: Object.values(newFavoriteFieldsByStream.fields).map((fields) => fields.length)
      });
      return StreamsActions.refresh();
    },
    onError: (errorThrown) => UserNotification.error(
      `Setting fields to favorites failed with error: ${errorThrown}`,
      "Could not set fields to favorites"
    )
  });
  const { mutate: addFavoriteField } = useMutation({
    mutationFn: (props) => FavoriteFields.add(props),
    onSuccess: () => {
      sendFavoriteFieldTelemetry("TOGGLED", {
        app_action_value: "add"
      });
      return StreamsActions.refresh();
    },
    onError: (errorThrown) => UserNotification.error(
      `Adding field to favorites failed with error: ${errorThrown}`,
      "Could not add field to favorites"
    )
  });
  const { mutate: removeFavoriteField } = useMutation({
    mutationFn: (props) => FavoriteFields.remove(props),
    onSuccess: () => {
      sendFavoriteFieldTelemetry("TOGGLED", {
        app_action_value: "remove"
      });
      return StreamsActions.refresh();
    },
    onError: (errorThrown) => UserNotification.error(
      `Removing field from favorites failed with error: ${errorThrown}`,
      "Could not remove field from favorites"
    )
  });
  const saveFavoriteField = useCallback(
    (favoritesToSave) => {
      const newAddedFields = favoritesToSave.filter((f) => !initialFavoriteFields.includes(f));
      const newFavoriteFieldsByStream = mapValues(
        initialFavoriteFieldsByStream,
        (streamFavoriteFields) => favoritesToSave.filter((f) => streamFavoriteFields.includes(f) || newAddedFields.includes(f))
      );
      setFavoriteFields({ fields: newFavoriteFieldsByStream });
    },
    [initialFavoriteFields, initialFavoriteFieldsByStream, setFavoriteFields]
  );
  const toggleField = useCallback(
    (field) => {
      const isFavorite = initialFavoriteFields == null ? void 0 : initialFavoriteFields.includes(field);
      const streamIds = Object.keys(initialFavoriteFieldsByStream);
      if (isFavorite) {
        removeFavoriteField({ field, stream_ids: streamIds });
      } else {
        addFavoriteField({ field, stream_ids: streamIds });
      }
    },
    [addFavoriteField, initialFavoriteFields, initialFavoriteFieldsByStream, removeFavoriteField]
  );
  return {
    setFieldsIsPending,
    saveFavoriteField,
    toggleField
  };
};
export default useMessageFavoriteFieldsMutation;
